import { Badge, Button, DataList, HStack, IconButton, NativeSelect, Text } from '@chakra-ui/react';
import { LuPencilLine } from 'react-icons/lu';
import { DialogBody, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from '@/components/ui/dialog';
import RoleBadge from '@/components/RoleBadge';
import StatusBadge from '@/components/StatusBadge';
import { Tooltip } from '@/components/ui/tooltip';
import { useState } from 'react';
import { User } from '@/api/User';
import { OrgUtil } from '@/data/OrgUtil';
import { userService, UserAccessRole, UserAccessStatus } from '@/service/UserService';

type UserAccessSectionProps = {
    user: User | null;
    canEdit: boolean;
    onUserChange: (user: User) => void;
};

const statusLabels: Record<string, string> = { active: 'Aktiv', disabled: 'Deaktiviert', suspended: 'Gesperrt', archived: 'Archiviert' };
const normalize = (value: unknown): string => String(value ?? '').toLowerCase();

const UserAccessSection = ({ user, canEdit, onUserChange }: UserAccessSectionProps) => {
    const currentStatus = normalize(user?.status);
    const currentRole = normalize(user?.role);
    const [status, setStatus] = useState(currentStatus);
    const [role, setRole] = useState(currentRole);
    const [isOpen, setIsOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const editable = canEdit && user !== null && !OrgUtil.isRoleRoot(user.role);
    const hasChanges = status !== currentStatus || role !== currentRole;
    const valid = ['active', 'disabled'].includes(status) && ['member', 'admin', 'extern'].includes(role);

    const edit = () => {
        setStatus(currentStatus);
        setRole(currentRole);
        setError(null);
        setIsOpen(true);
    };

    const save = async () => {
        if (!editable || !user || !valid || !hasChanges || isSaving) return;
        setIsSaving(true);
        setError(null);
        try {
            await userService.updateAccess(user.id, status as UserAccessStatus, role as UserAccessRole);
            onUserChange({ ...user, status, role });
            setIsOpen(false);
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Zugriff konnte nicht gespeichert werden.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <HStack gap='0.75rem'>
                {user ? <StatusBadge status={user.status} /> : <Badge>-</Badge>}
                {user ? <RoleBadge role={user.role} /> : <Badge>-</Badge>}
                {editable && (
                    <Tooltip content='Bearbeiten'>
                        <IconButton ms='auto' size='xs' variant='ghost' aria-label='Bearbeiten' onClick={edit}>
                            <LuPencilLine />
                        </IconButton>
                    </Tooltip>
                )}
            </HStack>
            {editable && (
                <DialogRoot open={isOpen} onOpenChange={details => { if (!isSaving) setIsOpen(details.open); }}
                    lazyMount unmountOnExit placement='center' size='sm'
                    closeOnEscape={!isSaving} closeOnInteractOutside={!isSaving}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Zugriff bearbeiten</DialogTitle>
                        </DialogHeader>
                        <DialogBody>
                            <DataList.Root orientation='horizontal' gap='0.75rem'>
                                <DataList.Item>
                                    <DataList.ItemLabel>Status</DataList.ItemLabel>
                                    <DataList.ItemValue>
                                        <NativeSelect.Root disabled={isSaving}>
                                            <NativeSelect.Field aria-label='Status' value={status} onChange={event => setStatus(event.currentTarget.value)}>
                                                {!['active', 'disabled'].includes(currentStatus) && <option value={currentStatus} disabled>{statusLabels[currentStatus] ?? currentStatus}</option>}
                                                <option value='active'>Aktiv</option>
                                                <option value='disabled'>Deaktiviert</option>
                                            </NativeSelect.Field>
                                            <NativeSelect.Indicator />
                                        </NativeSelect.Root>
                                    </DataList.ItemValue>
                                </DataList.Item>
                                <DataList.Item>
                                    <DataList.ItemLabel>Rolle</DataList.ItemLabel>
                                    <DataList.ItemValue>
                                        <NativeSelect.Root disabled={isSaving}>
                                            <NativeSelect.Field aria-label='Rolle' value={role} onChange={event => setRole(event.currentTarget.value)}>
                                                <option value='member'>Member</option>
                                                <option value='admin'>Admin</option>
                                                <option value='extern'>Partner</option>
                                            </NativeSelect.Field>
                                            <NativeSelect.Indicator />
                                        </NativeSelect.Root>
                                    </DataList.ItemValue>
                                </DataList.Item>
                            </DataList.Root>
                            {error && <Text role='alert' color='red.600' mt='0.75rem'>{error}</Text>}
                        </DialogBody>
                        <DialogFooter>
                            <Button variant='outline' colorPalette='gray' disabled={isSaving} onClick={() => setIsOpen(false)}>Abbrechen</Button>
                            <Button colorPalette='blue' loading={isSaving} disabled={!hasChanges || !valid} onClick={() => { void save(); }}>Speichern</Button>
                        </DialogFooter>
                    </DialogContent>
                </DialogRoot>
            )}
        </>
    );
};

export default UserAccessSection;
